import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Item } from '../../../../../api/items'
import { Job } from '../../../../../api/jobs'
import { Point } from '../../../../../api/types'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { useMobileMapProfile } from '../../../../../hooks/useMobileMapProfile'
import {
  PROGRESS_QUEUED_SAVE_COLOR,
} from '../../../../../utils/progressItemDisplay'
import {
  createPlanHitTester,
  type PlanHitTester,
} from '../../../../../utils/progressItemPlanHitTest'

const DRAG_SELECT_THRESHOLD_PX = 6

const generateLightingBoltSVGPath = (xOffset: number, yOffset: number) => {
  return `
    M ${19.375 + xOffset} ${13.7837 + yOffset}
    H ${11.0308 + xOffset}
    L ${15.0632 + xOffset} ${1.875 + yOffset}
    L ${0.625 + xOffset} ${17.8026 + yOffset}
    H ${8.96923 + xOffset}
    L ${4.94437 + xOffset} ${30 + yOffset}
    L ${19.375 + xOffset} ${13.7837 + yOffset}
  `
}

const generatePlusSignSVGPath = (xOffset: number, yOffset: number) => {
  return `
    M ${10 + xOffset} ${5 + yOffset}
    H ${15 + xOffset}
    V ${10 + yOffset}
    H ${20 + xOffset}
    V ${15 + yOffset}
    H ${15 + xOffset}
    V ${20 + yOffset}
    H ${10 + xOffset}
    V ${15 + yOffset}
    H ${5 + xOffset}
    V ${10 + yOffset}
    H ${10 + xOffset}
    Z
  `
}

const LINE_DRAW_WIDTH = 14

const seeThroughSlugPrefixes = [
  'ceiling_',
  'clutter',
  'lighting',
  'wet_surface',
  'heat_index',
  'fumes',
  'air_quality',
]

const RESIZE_DEBOUNCE_MS = 150

interface LayerManagerProps {
  jobs: Job[]
  setTooltipItem: (item: Item | null) => void
  onTooltipPosition?: (screenX: number, screenY: number) => void
  setHoverCoordinates?: (coordinates: { x: number; y: number }) => void
  selectedJobTypes: number[]
  activeTracker: string
  displayZeroValues?: boolean
  canvasId?: string
  observationDate?: Date
  selectedItemId?: number
  inDebugMode?: boolean
  queuedItemIds?: ReadonlySet<number> | number[]
  uninstalledColor?: string
  enableItemSelect?: boolean
  onClickItem?: (
    pointId: string,
    observationDate?: Date,
    activeTracker?: string,
    itemId?: string | number,
  ) => void
  onSelectItems?: (
    itemIds: number[],
    options?: { mode?: 'toggle' | 'add' },
  ) => void
  filteredPoints?: Point[]
}

type DrawContext = {
  ctx: CanvasRenderingContext2D
  inDebugMode: boolean
  selectedItemId?: number
  buildingPointId?: string
  buildingPointMap: Map<number, number>
  displayZeroValues: boolean
  queuedItemIds: ReadonlySet<number>
  dragPreviewItemIds: ReadonlySet<number>
  uninstalledColor?: string
}

type DragSelectState = {
  startClientX: number
  startClientY: number
  startPlanX: number
  startPlanY: number
  currentClientX: number
  currentClientY: number
  active: boolean
}

function drawSingleJob(job: Job, drawCtx: DrawContext) {
  if (job.completed_units < 0) {
    return
  }

  const {
    ctx,
    inDebugMode,
    selectedItemId,
    buildingPointId,
    buildingPointMap,
    queuedItemIds,
    dragPreviewItemIds,
    uninstalledColor,
  } = drawCtx

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let coords = job.item.position.map((p: any) => [p.x, p.y])
  const completedUnits = job.completed_units
  const isQueued = queuedItemIds.has(job.item.id)
  const isDragPreview = dragPreviewItemIds.has(job.item.id)
  const isHighlighted = isQueued || isDragPreview

  const {
    display_shape: displayShape,
    slug,
    display_color,
    secondary_display_color,
    tertiary_display_color,
  } = job.type.job_type

  const drawPath = displayShape === 'line' || displayShape === 'area'
  const meetsCompletedUnitsThreshold =
    drawCtx.displayZeroValues || completedUnits > 0 || isHighlighted
  const isWetSurface = slug.startsWith('wet_surface')
  const seeThrough = seeThroughSlugPrefixes.some(value =>
    slug.startsWith(value),
  )

  if (!coords[0] || !meetsCompletedUnitsThreshold) {
    return
  }

  const highlightColor = isHighlighted ? PROGRESS_QUEUED_SAVE_COLOR : null
  const remainingColor =
    !isHighlighted && completedUnits <= 0 && uninstalledColor
      ? uninstalledColor
      : null

  const path = new Path2D()
  let strokeStyle = highlightColor ?? remainingColor ?? display_color
  let fillStyle =
    highlightColor ?? remainingColor ?? (secondary_display_color ?? display_color)

  if (inDebugMode && !isHighlighted) {
    const itemNotSelectedItem = selectedItemId && job.item.id !== selectedItemId
    const itemSubPointId = buildingPointMap.get(job.item.viewpoint)
    const itemNotPartOfSelectedPoint =
      !selectedItemId &&
      buildingPointId &&
      itemSubPointId &&
      parseInt(buildingPointId) !== itemSubPointId

    const reduceOpacity = itemNotSelectedItem || itemNotPartOfSelectedPoint

    if (reduceOpacity) {
      if (displayShape === 'line' || displayShape === 'point') {
        strokeStyle = `${strokeStyle}33`
      } else if (displayShape === 'area' || displayShape === 'point') {
        fillStyle = `${fillStyle}33`
      }
    }
  }

  if (isWetSurface && job.completed_units === 1 && !isHighlighted) {
    fillStyle = `${fillStyle}00`
  }

  ctx.strokeStyle = strokeStyle
  ctx.fillStyle = fillStyle
  ctx.lineWidth = LINE_DRAW_WIDTH
  ctx.lineCap = 'butt'
  ctx.globalAlpha = 1

  if (drawPath) {
    path.moveTo(coords[0][0], coords[0][1])
    coords = coords.slice(1)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    coords.forEach((point: any) => {
      path.lineTo(point[0], point[1])
    })
    if (displayShape === 'line') {
      ctx.stroke(path)
    } else if (displayShape === 'area') {
      if (seeThrough) {
        ctx.globalAlpha = 0.5
      }
      ctx.closePath()
      ctx.fill(path)
      ctx.globalAlpha = 1
    }
  } else if (displayShape === 'point') {
    ctx.lineWidth = 4
    ctx.strokeStyle = fillStyle
    ctx.fillStyle = strokeStyle
    path.arc(coords[0][0], coords[0][1], 22, 0, 2 * Math.PI)
    ctx.stroke(path)

    const fillingPath = new Path2D()
    fillingPath.arc(coords[0][0], coords[0][1], 20, 0, 2 * Math.PI)
    ctx.fill(fillingPath)

    ctx.fillStyle = tertiary_display_color ?? strokeStyle

    if (slug.startsWith('electrical')) {
      const lightningBoltPath = new Path2D(
        generateLightingBoltSVGPath(
          parseFloat(coords[0][0]) - 9.5,
          parseFloat(coords[0][1]) - 16,
        ),
      )
      ctx.fill(lightningBoltPath)
    } else {
      const plusSignPath = new Path2D(
        generatePlusSignSVGPath(
          parseFloat(coords[0][0]) - 12.5,
          parseFloat(coords[0][1]) - 12,
        ),
      )
      ctx.fill(plusSignPath)
    }
  }
}

function buildDummyJobs(data: Job[]): Job[] {
  const dummyJobs: Job[] = []
  const seenItems = new Set<number>()

  data.forEach((job: Job) => {
    const itemId = job.item.id
    if (!seenItems.has(itemId)) {
      dummyJobs.push({
        ...job,
        completed_units: 1,
        type: {
          ...job.type,
          job_type: {
            ...job.type.job_type,
            display_order: Number.MIN_SAFE_INTEGER,
            display_color: '#808080',
            secondary_display_color: job.type.job_type.slug.startsWith(
              'electrical',
            )
              ? '#e8e1e1'
              : '#808080',
            tertiary_display_color: '#d1cbcb',
          },
        },
      })
      seenItems.add(itemId)
    }
  })

  return dummyJobs
}

function sortJobsForDraw(jobs: Job[]): Job[] {
  return [...jobs].sort((a, b) => {
    try {
      return a.type.job_type.display_order < b.type.job_type.display_order
        ? -1
        : 1
    } catch {
      return 0
    }
  })
}

function scheduleIdle(): Promise<void> {
  return new Promise(resolve => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => resolve(), { timeout: 50 })
    } else {
      requestAnimationFrame(() => resolve())
    }
  })
}

export const LayerManager = ({
  jobs,
  setTooltipItem,
  onTooltipPosition,
  setHoverCoordinates,
  selectedJobTypes,
  activeTracker,
  displayZeroValues = false,
  canvasId = 'progress-canvas',
  inDebugMode = false,
  observationDate,
  selectedItemId,
  queuedItemIds,
  uninstalledColor,
  enableItemSelect = false,
  onClickItem,
  onSelectItems,
  filteredPoints,
}: LayerManagerProps) => {
  const mobileProfile = useMobileMapProfile()
  const planHitTesterRef = useRef<PlanHitTester | null>(null)
  const drawScaleRef = useRef(1)
  const drawGenerationRef = useRef(0)
  const lastPointerEventRef = useRef<PointerEvent | MouseEvent | null>(null)
  const rafIdRef = useRef<number | null>(null)
  const checkHoveringRef = useRef<
    (clientX: number, clientY: number) => Item | null
  >(() => null)
  const setTooltipItemRef = useRef(setTooltipItem)
  const setHoverCoordinatesRef = useRef(setHoverCoordinates)
  const onTooltipPositionRef = useRef(onTooltipPosition)
  const dragSelectRef = useRef<DragSelectState | null>(null)
  const dragPreviewRafRef = useRef<number | null>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [overlayDrawing, setOverlayDrawing] = useState(false)
  const [dragSelectBox, setDragSelectBox] = useState<{
    left: number
    top: number
    width: number
    height: number
  } | null>(null)
  const [dragPreviewItemIds, setDragPreviewItemIds] = useState<number[]>([])
  const { state: buildingState } = useBuildingContext()

  const queuedItemIdSet = useMemo(() => {
    if (!queuedItemIds) {
      return new Set<number>()
    }
    return queuedItemIds instanceof Set
      ? queuedItemIds
      : new Set(queuedItemIds)
  }, [queuedItemIds])

  const dragPreviewItemIdSet = useMemo(
    () => new Set(dragPreviewItemIds),
    [dragPreviewItemIds],
  )

  setTooltipItemRef.current = setTooltipItem
  setHoverCoordinatesRef.current = setHoverCoordinates
  onTooltipPositionRef.current = onTooltipPosition

  const buildingPointMap = useMemo(() => {
    const pointMap = new Map<number, number>()
    const pointsToUse = filteredPoints || buildingState.floorData.points
    if (pointsToUse) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pointsToUse.forEach((point: any) => {
        pointMap.set(point.id, point.point_id)
      })
    }
    return pointMap
  }, [buildingState.floorData.points, filteredPoints])

  const useLineForMechanical = useMemo(() => {
    const oldProjectIds = [
      'myFE3eLIhA',
      'xVrtPeVag',
      'Cdfrk7G',
      'nNkkv7gmQ',
      'rVHF8Zfxz',
      '2MEQPeZxFv',
      '3FD6QNa',
    ]
    return (
      oldProjectIds.includes(buildingState.projectId) &&
      activeTracker === 'Mechanical'
    )
  }, [buildingState.projectId, activeTracker])

  useEffect(() => {
    if (jobs && useLineForMechanical) {
      jobs.forEach(job => {
        if (job.type.job_type.display_shape === 'area') {
          job.type.job_type.display_shape = 'line'
        }
      })
    }
  }, [jobs, useLineForMechanical])

  const jobsForSelectedTypes = useMemo(() => {
    if (jobs) {
      return jobs.filter(job => selectedJobTypes.includes(job.type.job_type.id))
    }
    return []
  }, [jobs, selectedJobTypes])

  const canvasMetrics = useMemo(() => {
    const displayW = dimensions.width
    const displayH = dimensions.height
    if (!displayW || !displayH) {
      return {
        canvasWidth: 0,
        canvasHeight: 0,
        drawScale: 1,
      }
    }

    const cap = mobileProfile.capCanvasDimension
    const maxDim = Math.max(displayW, displayH)
    const drawScale = cap != null && maxDim > cap ? cap / maxDim : 1

    return {
      canvasWidth: Math.max(1, Math.round(displayW * drawScale)),
      canvasHeight: Math.max(1, Math.round(displayH * drawScale)),
      drawScale,
    }
  }, [dimensions, mobileProfile.capCanvasDimension])

  const runDraw = useCallback(
    async (data: Job[]) => {
      const generation = ++drawGenerationRef.current
      const c = document.getElementById(
        canvasId,
      ) as HTMLCanvasElement | null
      if (!c || !canvasMetrics.canvasWidth) {
        return
      }

      const ctx = c.getContext('2d')
      if (!ctx) {
        return
      }

      const { canvasWidth, canvasHeight, drawScale } = canvasMetrics
      drawScaleRef.current = drawScale

      const dummyJobs = mobileProfile.skipDummyLayer ? [] : buildDummyJobs(data)
      const sorted = sortJobsForDraw([...data, ...dummyJobs])

      const drawCtx: DrawContext = {
        ctx,
        inDebugMode,
        selectedItemId,
        buildingPointId: buildingState.pointId,
        buildingPointMap,
        displayZeroValues,
        queuedItemIds: queuedItemIdSet,
        dragPreviewItemIds: dragPreviewItemIdSet,
        uninstalledColor,
      }

      setOverlayDrawing(mobileProfile.chunkedDraw && sorted.length > 50)
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvasWidth, canvasHeight)
      ctx.setTransform(drawScale, 0, 0, drawScale, 0, 0)

      if (
        !mobileProfile.chunkedDraw ||
        sorted.length <= mobileProfile.chunkSize
      ) {
        sorted.forEach(job => drawSingleJob(job, drawCtx))
      } else {
        for (let i = 0; i < sorted.length; i += mobileProfile.chunkSize) {
          if (generation !== drawGenerationRef.current) {
            return
          }
          const slice = sorted.slice(i, i + mobileProfile.chunkSize)
          slice.forEach(job => drawSingleJob(job, drawCtx))
          await scheduleIdle()
        }
      }

      if (generation !== drawGenerationRef.current) {
        return
      }

      planHitTesterRef.current = createPlanHitTester(data, selectedJobTypes)
      setOverlayDrawing(false)
    },
    [
      buildingPointMap,
      buildingState.pointId,
      canvasId,
      canvasMetrics,
      displayZeroValues,
      inDebugMode,
      mobileProfile.chunkSize,
      mobileProfile.chunkedDraw,
      mobileProfile.skipDummyLayer,
      queuedItemIdSet,
      dragPreviewItemIdSet,
      selectedItemId,
      uninstalledColor,
      selectedJobTypes,
    ],
  )

  const getPlanCoords = useCallback(
    (canvas: HTMLElement, clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect()
      const canvasEl = canvas as HTMLCanvasElement
      const scaleX = canvasEl.width / rect.width
      const scaleY = canvasEl.height / rect.height
      const canvasX = (clientX - rect.left) * scaleX
      const canvasY = (clientY - rect.top) * scaleY
      const drawScale = drawScaleRef.current || 1
      return {
        planX: canvasX / drawScale,
        planY: canvasY / drawScale,
        tooltipX: clientX,
        tooltipY: clientY,
      }
    },
    [],
  )

  const checkHovering = useCallback(
    (clientX: number, clientY: number) => {
      const canvasElement = document.getElementById(
        canvasId,
      ) as HTMLCanvasElement | null
      if (!canvasElement) {
        return null
      }

      const tester = planHitTesterRef.current
      if (!tester) {
        canvasElement.style.cursor = 'default'
        return null
      }

      const { planX, planY } = getPlanCoords(canvasElement, clientX, clientY)
      const item = tester.hitTest(planX, planY)
      canvasElement.style.cursor = item ? 'pointer' : 'default'
      return item
    },
    [canvasId, getPlanCoords],
  )

  checkHoveringRef.current = checkHovering

  const throttledHoverHandler = useCallback(
    (e: MouseEvent) => {
      if (dragSelectRef.current) {
        return
      }
      lastPointerEventRef.current = e
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null
          const evt = lastPointerEventRef.current
          if (!evt) {
            return
          }
          const canvas = document.getElementById(canvasId)
          if (canvas) {
            const pos = getPlanCoords(canvas, evt.clientX, evt.clientY)
            if (onTooltipPositionRef.current) {
              onTooltipPositionRef.current(pos.tooltipX, pos.tooltipY)
            } else if (setHoverCoordinatesRef.current) {
              setHoverCoordinatesRef.current({
                x: pos.tooltipX,
                y: pos.tooltipY,
              })
            }
          }
          const item = checkHoveringRef.current(evt.clientX, evt.clientY)
          setTooltipItemRef.current(item)
        })
      }
    },
    [canvasId, getPlanCoords],
  )

  const emitSelectOrNavigate = useCallback(
    (item: Item) => {
      if (enableItemSelect && onSelectItems) {
        onSelectItems([item.id], { mode: 'toggle' })
        return
      }
      if (!onClickItem) {
        return
      }
      const pointSubId = buildingPointMap.get(item.viewpoint)
      if (pointSubId) {
        onClickItem(
          pointSubId.toString(),
          observationDate,
          activeTracker,
          item.id,
        )
      }
    },
    [
      activeTracker,
      buildingPointMap,
      enableItemSelect,
      observationDate,
      onClickItem,
      onSelectItems,
    ],
  )

  const finishDragSelect = useCallback(
    (endClientX: number, endClientY: number) => {
      const drag = dragSelectRef.current
      dragSelectRef.current = null
      setDragSelectBox(null)
      setDragPreviewItemIds([])
      if (dragPreviewRafRef.current != null) {
        cancelAnimationFrame(dragPreviewRafRef.current)
        dragPreviewRafRef.current = null
      }

      const canvas = document.getElementById(
        canvasId,
      ) as HTMLCanvasElement | null
      if (canvas) {
        canvas.style.cursor = 'default'
      }

      if (!drag) {
        return
      }

      const tester = planHitTesterRef.current
      if (!canvas || !tester) {
        return
      }

      const dx = endClientX - drag.startClientX
      const dy = endClientY - drag.startClientY
      const movedFarEnough =
        dx * dx + dy * dy >= DRAG_SELECT_THRESHOLD_PX * DRAG_SELECT_THRESHOLD_PX

      if (!movedFarEnough) {
        const item = checkHovering(drag.startClientX, drag.startClientY)
        if (item) {
          emitSelectOrNavigate(item)
        }
        return
      }

      if (!enableItemSelect || !onSelectItems) {
        return
      }

      const end = getPlanCoords(canvas, endClientX, endClientY)
      const items = tester.hitTestRect(
        drag.startPlanX,
        drag.startPlanY,
        end.planX,
        end.planY,
      )
      if (items.length) {
        onSelectItems(
          items.map(item => item.id),
          { mode: 'add' },
        )
      }
    },
    [
      canvasId,
      checkHovering,
      emitSelectOrNavigate,
      enableItemSelect,
      getPlanCoords,
      onSelectItems,
    ],
  )

  const updateDragSelectVisuals = useCallback(
    (clientX: number, clientY: number) => {
      const drag = dragSelectRef.current
      if (!drag?.active) {
        return
      }

      const left = Math.min(drag.startClientX, clientX)
      const top = Math.min(drag.startClientY, clientY)
      setDragSelectBox({
        left,
        top,
        width: Math.abs(clientX - drag.startClientX),
        height: Math.abs(clientY - drag.startClientY),
      })

      if (dragPreviewRafRef.current != null) {
        return
      }

      dragPreviewRafRef.current = requestAnimationFrame(() => {
        dragPreviewRafRef.current = null
        const activeDrag = dragSelectRef.current
        const canvas = document.getElementById(
          canvasId,
        ) as HTMLCanvasElement | null
        const tester = planHitTesterRef.current
        if (!activeDrag?.active || !canvas || !tester) {
          return
        }

        const end = getPlanCoords(
          canvas,
          activeDrag.currentClientX,
          activeDrag.currentClientY,
        )
        const items = tester.hitTestRect(
          activeDrag.startPlanX,
          activeDrag.startPlanY,
          end.planX,
          end.planY,
        )
        setDragPreviewItemIds(items.map(item => item.id))
      })
    },
    [canvasId, getPlanCoords],
  )

  const handlePointerDown = useCallback(
    (e: PointerEvent | MouseEvent) => {
      if (!enableItemSelect) {
        return
      }
      if (!e.shiftKey || e.button !== 0) {
        return
      }

      const canvas = document.getElementById(
        canvasId,
      ) as HTMLCanvasElement | null
      if (!canvas) {
        return
      }

      e.preventDefault()
      e.stopPropagation()
      if (typeof (e as MouseEvent).stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation()
      }

      const { planX, planY } = getPlanCoords(canvas, e.clientX, e.clientY)
      dragSelectRef.current = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPlanX: planX,
        startPlanY: planY,
        currentClientX: e.clientX,
        currentClientY: e.clientY,
        active: false,
      }
      canvas.style.cursor = 'pointer'
      setDragPreviewItemIds([])
      setTooltipItem(null)
    },
    [canvasId, enableItemSelect, getPlanCoords, setTooltipItem],
  )

  const handlePointerMove = useCallback(
    (e: PointerEvent | MouseEvent) => {
      const drag = dragSelectRef.current
      if (!drag) {
        return
      }

      e.preventDefault()
      e.stopPropagation()
      if (typeof (e as MouseEvent).stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation()
      }

      drag.currentClientX = e.clientX
      drag.currentClientY = e.clientY
      const dx = e.clientX - drag.startClientX
      const dy = e.clientY - drag.startClientY
      if (
        !drag.active &&
        dx * dx + dy * dy >= DRAG_SELECT_THRESHOLD_PX * DRAG_SELECT_THRESHOLD_PX
      ) {
        drag.active = true
      }
      if (drag.active) {
        updateDragSelectVisuals(e.clientX, e.clientY)
      }
    },
    [updateDragSelectVisuals],
  )

  const handlePointerUp = useCallback(
    (e: PointerEvent | MouseEvent) => {
      if (!dragSelectRef.current) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      if (typeof (e as MouseEvent).stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation()
      }
      finishDragSelect(e.clientX, e.clientY)
    },
    [finishDragSelect],
  )

  const handlePointerTap = useCallback(
    (e: PointerEvent) => {
      if (dragSelectRef.current) {
        return
      }
      const item = checkHovering(e.clientX, e.clientY)
      if (!item) {
        return
      }
      emitSelectOrNavigate(item)
    },
    [checkHovering, emitSelectOrNavigate],
  )

  const handleItemClick = useCallback(
    (e: MouseEvent) => {
      if (dragSelectRef.current) {
        return
      }
      if (enableItemSelect && (e as MouseEvent).shiftKey) {
        return
      }
      const item = checkHovering(e.clientX, e.clientY)
      if (!item) {
        return
      }
      emitSelectOrNavigate(item)
    },
    [checkHovering, emitSelectOrNavigate, enableItemSelect],
  )

  const canvasRef = useCallback(
    (node: HTMLCanvasElement | null) => {
      if (node !== null && canvasMetrics.canvasHeight !== 0) {
        void runDraw(jobsForSelectedTypes)
      }
    },
    [canvasMetrics.canvasHeight, jobsForSelectedTypes, runDraw],
  )

  useEffect(() => {
    const fp = document.getElementById('floor-plan')
    if (!fp || !jobs) {
      return
    }

    let timeoutId: number | null = null
    const setDim = () => {
      if (timeoutId != null) {
        window.clearTimeout(timeoutId)
      }
      timeoutId = window.setTimeout(() => {
        setDimensions({ width: fp.clientWidth, height: fp.clientHeight })
      }, RESIZE_DEBOUNCE_MS)
    }

    setDimensions({ width: fp.clientWidth, height: fp.clientHeight })

    const resizeObserver = new ResizeObserver(setDim)
    resizeObserver.observe(fp)
    return () => {
      resizeObserver.disconnect()
      if (timeoutId != null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [jobs])

  useEffect(() => {
    if (canvasMetrics.canvasHeight !== 0) {
      void runDraw(jobsForSelectedTypes)
    }
  }, [jobsForSelectedTypes, canvasMetrics, runDraw])

  useEffect(() => {
    const canvas = document.getElementById(canvasId)
    if (!canvas) {
      return
    }

    if (mobileProfile.disableHover) {
      const onPointerUp = (e: Event) => handlePointerTap(e as PointerEvent)
      canvas.addEventListener('pointerup', onPointerUp)
      return () => {
        canvas.removeEventListener('pointerup', onPointerUp)
      }
    }

    const onPointerDown = (e: Event) => handlePointerDown(e as PointerEvent)
    const onPointerMove = (e: Event) => handlePointerMove(e as PointerEvent)
    const onPointerUp = (e: Event) => handlePointerUp(e as PointerEvent)
    const onMouseDown = (e: Event) => handlePointerDown(e as MouseEvent)
    const onMouseMove = (e: Event) => handlePointerMove(e as MouseEvent)
    const onMouseUp = (e: Event) => handlePointerUp(e as MouseEvent)

    canvas.addEventListener('mousemove', throttledHoverHandler)
    canvas.addEventListener('click', handleItemClick)
    if (enableItemSelect) {
      canvas.addEventListener('pointerdown', onPointerDown)
      canvas.addEventListener('mousedown', onMouseDown, true)
      window.addEventListener('pointermove', onPointerMove, true)
      window.addEventListener('pointerup', onPointerUp, true)
      window.addEventListener('mousemove', onMouseMove, true)
      window.addEventListener('mouseup', onMouseUp, true)
    }
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      if (dragPreviewRafRef.current != null) {
        cancelAnimationFrame(dragPreviewRafRef.current)
        dragPreviewRafRef.current = null
      }
      canvas.removeEventListener('mousemove', throttledHoverHandler)
      canvas.removeEventListener('click', handleItemClick)
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('mousedown', onMouseDown, true)
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', onPointerUp, true)
      window.removeEventListener('mousemove', onMouseMove, true)
      window.removeEventListener('mouseup', onMouseUp, true)
    }
  }, [
    canvasId,
    enableItemSelect,
    handleItemClick,
    handlePointerDown,
    handlePointerMove,
    handlePointerTap,
    handlePointerUp,
    mobileProfile.disableHover,
    throttledHoverHandler,
  ])

  return (
    <>
      <canvas
        id={canvasId}
        ref={canvasRef}
        width={canvasMetrics.canvasWidth}
        height={canvasMetrics.canvasHeight}
        style={{ position: 'absolute', left: 0 }}
      />
      {dragSelectBox && (
        <div
          style={{
            position: 'fixed',
            left: dragSelectBox.left,
            top: dragSelectBox.top,
            width: dragSelectBox.width,
            height: dragSelectBox.height,
            border: `1.5px dashed ${PROGRESS_QUEUED_SAVE_COLOR}`,
            background: 'rgba(255, 107, 0, 0.12)',
            boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.55)',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        />
      )}
      {overlayDrawing && (
        <div
          style={{
            position: 'absolute',
            left: 8,
            bottom: 8,
            padding: '4px 8px',
            fontSize: 11,
            color: '#686868',
            background: 'rgba(255,255,255,0.9)',
            borderRadius: 4,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          Loading overlays…
        </div>
      )}
    </>
  )
}
